import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { modules } from "byzantine";
import User from "byzantine/src/User";
import type {
  WebSectionLeft,
  WebSectionRight,
} from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences/types";

import { useCurrentUser } from "../../contexts/CurrentUserContext";
import type { DashboardCustomizationProps } from "./types";
import useIsMarketingBannersEnabled from "../MarketingImage/hooks";
import useIsMarketingCardVisible from "../MarketingCardContainer/hooks";
import useIsChartCardEnabled from "../ChartCard.hooks";
import useIsServicesEnabled from "../ServicesCard.hooks";
import useCardLinkedOffersFeature from "../../CardLinkedOffers/hooks";
import useIsCreditMonitoringEnabled from "../../savvy_money/hooks/useIsCreditMonitoringEnabled";
import useIsSubscriptionManagementEnabled from "../../subscriptions/SubscriptionCard/hooks";

const {
  useCreateEditUserDisplayPreferences,
  useIsDashboardCustomizationEnabled,
  applyPositionOverrides,
  SECTION,
  WEB_DASHBOARD_SECTIONS_LEFT,
  WEB_DASHBOARD_SECTIONS_RIGHT,
  enforceVisibleSections,
} = modules.userDisplayPreferences;
const { useExternalAccountsOnDashboardEnabled } = modules.externalAccounts;

/**
 * Will return the default columns sorting or a user defined columns sorting.
 * Each allows for positional overrides via the
 * `olb_dashboard_customization_locked_positions` feature flag.
 */
export const useWebDashboardSortOrder = (): {
  leftOrder: WebSectionLeft[];
  rightOrder: WebSectionRight[];
} => {
  const { currentUser } = useCurrentUser();
  const {
    isDashboardCustomizationEnabled,
    dashboardCustomizationLockedFields,
  } = useIsDashboardCustomizationEnabled();
  const displayPreferences = currentUser?.user_display_preferences;
  const lockedPositions =
    dashboardCustomizationLockedFields?.web_home_item_positions;

  return useMemo(() => {
    const savedLeft = displayPreferences?.web_home_order_left;
    const savedRight = displayPreferences?.web_home_order_right;

    const leftBase: WebSectionLeft[] =
      isDashboardCustomizationEnabled && savedLeft && savedLeft.length > 0
        ? [...savedLeft]
        : [...WEB_DASHBOARD_SECTIONS_LEFT];
    const rightBase: WebSectionRight[] =
      isDashboardCustomizationEnabled && savedRight && savedRight.length > 0
        ? [...savedRight]
        : [...WEB_DASHBOARD_SECTIONS_RIGHT];

    const overrides = lockedPositions ?? {};

    return {
      leftOrder: applyPositionOverrides(
        leftBase,
        overrides
      ) as WebSectionLeft[],
      rightOrder: applyPositionOverrides(
        rightBase,
        overrides
      ) as WebSectionRight[],
    };
  }, [isDashboardCustomizationEnabled, displayPreferences, lockedPositions]);
};

export const useVisibleWebDashboardSections = ({
  externalAccounts,
  servicesApps,
}: DashboardCustomizationProps) => {
  const { leftOrder, rightOrder } = useWebDashboardSortOrder();
  const isMarketingBannersEnabled = useIsMarketingBannersEnabled();
  const isMarketingCardVisible = useIsMarketingCardVisible();
  const isChartCardEnabled = useIsChartCardEnabled();
  const isCreditMonitoringEnabled = useIsCreditMonitoringEnabled();
  const isServicesEnabled = useIsServicesEnabled(servicesApps);
  const isSubscriptionManagementEnabled = useIsSubscriptionManagementEnabled();
  const isCardLinkedOffersEnabled = useCardLinkedOffersFeature();
  const isExternalAccountsOnDashboardEnabled =
    useExternalAccountsOnDashboardEnabled();

  const hasUnverifiedExternalAccounts = useMemo(
    () => externalAccounts.some((account) => !account.verified),
    [externalAccounts]
  );

  const leftColumnSectionVisibility = useMemo<Record<WebSectionLeft, boolean>>(
    () => ({
      [SECTION.MARKETING_BANNER_IMAGE]: isMarketingBannersEnabled,
      [SECTION.ACCOUNTS]: true,
      [SECTION.EXTERNAL_ACCOUNTS]: isExternalAccountsOnDashboardEnabled,
      [SECTION.RECENT_TRANSACTIONS]: true,
      [SECTION.CARD_OFFERS]: isCardLinkedOffersEnabled,
    }),
    [
      isMarketingBannersEnabled,
      isExternalAccountsOnDashboardEnabled,
      isCardLinkedOffersEnabled,
    ]
  );

  const rightColumnSectionVisibility = useMemo<
    Record<WebSectionRight, boolean>
  >(
    () => ({
      [SECTION.ACCOUNT_TOTALS]: true,
      [SECTION.CHART_CARD]: isChartCardEnabled,
      [SECTION.CREDIT_MONITORING]: isCreditMonitoringEnabled,
      [SECTION.VERIFY_EXTERNAL_ACCOUNTS]:
        !isExternalAccountsOnDashboardEnabled && hasUnverifiedExternalAccounts,
      [SECTION.MARKETING_CARD]: isMarketingCardVisible,
      [SECTION.SERVICES]: isServicesEnabled,
      [SECTION.SUBSCRIPTIONS]: isSubscriptionManagementEnabled,
      [SECTION.MARKETING_CARD_IMAGE]: isMarketingBannersEnabled,
    }),
    [
      isCardLinkedOffersEnabled,
      isChartCardEnabled,
      isCreditMonitoringEnabled,
      isExternalAccountsOnDashboardEnabled,
      isMarketingBannersEnabled,
      isMarketingCardVisible,
      isServicesEnabled,
      isSubscriptionManagementEnabled,
      hasUnverifiedExternalAccounts,
    ]
  );

  const visibleLeftSectionIds: WebSectionLeft[] = useMemo(() => {
    const visibleLeftSections = Object.entries(leftColumnSectionVisibility)
      .filter(([, isVisible]) => isVisible)
      .map(([section]) => section as WebSectionLeft);
    return enforceVisibleSections(leftOrder, visibleLeftSections);
  }, [leftOrder, leftColumnSectionVisibility]);

  const visibleRightSectionIds: WebSectionRight[] = useMemo(() => {
    const visibleRightSections = Object.entries(rightColumnSectionVisibility)
      .filter(([, isVisible]) => isVisible)
      .map(([section]) => section as WebSectionRight);
    return enforceVisibleSections(rightOrder, visibleRightSections);
  }, [rightOrder, rightColumnSectionVisibility]);

  const isPageReloadRequired = useMemo(
    () => !!isChartCardEnabled,
    [isChartCardEnabled]
  );

  return {
    isPageReloadRequired,
    visibleLeftSectionIds,
    visibleRightSectionIds,
  };
};

// assists with displaying items with async checks
const mergeNewlyVisible = <T extends string>(
  current: T[],
  visibleIds: T[]
): T[] => {
  if (visibleIds.length <= current.length) return current;
  const currentSet = new Set(current);
  const missing = visibleIds.filter((id) => !currentSet.has(id));
  return [...current, ...missing];
};

const useDashboardCustomization = ({
  externalAccounts,
  servicesApps,
}: DashboardCustomizationProps) => {
  const { currentUser, setCurrentUser } = useCurrentUser();
  const { user_display_preferences: displayPreferences } = currentUser || {};
  const userCustomizationExists = useRef(!!displayPreferences);
  const hasUserEdits = useRef(false);
  const [isCustomizeDialogOpen, setIsCustomizeDialogOpen] =
    useState<boolean>(false);
  const { createEditUserDisplayPreferences, loading } =
    useCreateEditUserDisplayPreferences();

  const {
    isPageReloadRequired,
    visibleLeftSectionIds,
    visibleRightSectionIds,
  } = useVisibleWebDashboardSections({ externalAccounts, servicesApps });

  const [leftOrder, setLeftOrderState] = useState<WebSectionLeft[]>(
    visibleLeftSectionIds
  );
  const [rightOrder, setRightOrderState] = useState<WebSectionRight[]>(
    visibleRightSectionIds
  );

  // Loads async items into available items to drag
  // SUBSCRIPTIONS, SERVICES, and VERIFY_EXTERNAL_ACCOUNTS
  useEffect(() => {
    setRightOrderState((current) =>
      mergeNewlyVisible(current, visibleRightSectionIds)
    );
  }, [visibleRightSectionIds]);

  const setLeftOrder = useCallback((next: WebSectionLeft[]) => {
    hasUserEdits.current = true;
    setLeftOrderState(next);
  }, []);

  const setRightOrder = useCallback((next: WebSectionRight[]) => {
    hasUserEdits.current = true;
    setRightOrderState(next);
  }, []);

  const openDialogHandler = () => {
    // Reseed from latest visible sections. The persistent provider means we
    // can't rely on mount-time initialization the way atlas does.
    setLeftOrderState(visibleLeftSectionIds);
    setRightOrderState(visibleRightSectionIds);
    hasUserEdits.current = false;
    setIsCustomizeDialogOpen(true);
  };

  const closeDialogHandler = () => {
    setIsCustomizeDialogOpen(false);
    hasUserEdits.current = false;
  };

  const submitCustomizationHandler = (forcePageReload = false) => {
    const payload = {
      web_home_order_left: leftOrder,
      web_home_order_right: rightOrder,
    };
    createEditUserDisplayPreferences(payload, userCustomizationExists.current, {
      onSuccess() {
        setCurrentUser(
          new User({
            ...currentUser,
            user_display_preferences: {
              mobile_app_home_order:
                displayPreferences?.mobile_app_home_order ?? [],
              web_home_order_left: leftOrder,
              web_home_order_right: rightOrder,
            },
          })
        );
        userCustomizationExists.current = true;
        hasUserEdits.current = false;
        setIsCustomizeDialogOpen(false);

        if (forcePageReload) {
          window.location.reload();
        }
      },
    });
  };

  return {
    isCustomizeDialogOpen,
    isPageReloadRequired,
    openDialogHandler,
    closeDialogHandler,
    submitCustomizationHandler,
    isCustomizeLoading: loading,
    leftOrder,
    rightOrder,
    setLeftOrder,
    setRightOrder,
  };
};

export default useDashboardCustomization;
